import { inject, Injectable } from '@angular/core';
import { InterpolationParameters, TranslateService } from '@ngx-translate/core';
import { from, interval, merge, Observable, of } from 'rxjs';
import { debounceTime, map, take } from 'rxjs/operators';

import {
    BuiltInFieldName, FieldOperator, IAdvancedSearchRequest, IApiServiceConfig, IOrganization, IPagedResponse,
    ISearchOrganizationsRequest,
    IStage, SortDirection
} from '@azavista/planner-lib';
import { AzavistaInputFieldService } from '@azavista/components/input-field';
import {
    AzavistaSharedService, FieldComponentType, IAdvancedFilterCriteria, IAzavistaAdvancedSearchComponentChangedData, IField,
    IFieldIdWithOptions, IObjectEnumInputFieldValue, IObjectWithId, IPagingOptions, ISortingChangedData, ITableSettingsChangedData,
    IValueTranslation, InputFieldType
} from '@azavista/components/shared';
import { ITableColumnCustomCssMap } from '@azavista/components/table';
import { FlatSearchParams, flatSearchToRecursive, SearchParams } from '@azavista/advanced-search';
import { TranslationKey } from './enums';
import { environment } from '../../environments/environment';
import { MIN_PRINT_SERVER_VERSION, TranslationMapKey } from './const';
import { IVersionInfo } from '../print-server-api/interfaces';
import { IIsPrintServerVersionSupportedResult } from './interfaces';
import { OnsiteCheckInClientService } from './components/onsite-checkin-client/onsite-checkin-client.service';
import { OnsiteCheckInClientAuthService } from './components/onsite-checkin-client/onsite-checkin-client-auth.service';

export const delay = async (ms: number) => new Promise<void>((resolve) => {
    window.setTimeout(() => resolve(), ms);
});

/**
 * @returns Stream of remaining time in milliseconds.
 */
export const countDownAction = (delayMs: number, action: () => void, intervalPeriodMs = 1000): Observable<number> => {
    const interval$ = interval(intervalPeriodMs).pipe(
        map((_, index) => (delayMs - ((index + 1) * intervalPeriodMs))),
        take(Math.floor(delayMs / intervalPeriodMs)),
    );
    const startAction$ = from(delay(delayMs)).pipe(
        map(() => {
            action();
            return 0;
        })
    )
    return merge(of(delayMs),interval$, startAction$);
}

@Injectable({
    providedIn: 'root'
})
export class SharedService {
    clientSvc = inject(OnsiteCheckInClientService);
    authSvc = inject(OnsiteCheckInClientAuthService);

    constructor(
        private readonly translateSvc: TranslateService,
        private readonly azavistaSharedSvc: AzavistaSharedService,
        private readonly inputFldSvc: AzavistaInputFieldService,
        // private readonly matBottomSheet: MatBottomSheet,
        private readonly cmpSharedSvc: AzavistaSharedService,
    ) {
    }

    getQuickSearchTextFieldNames(fields: IField[], displayedIds: string[]): string[] {
        const textFieldTypes: InputFieldType[] = ['text', 'email'];
        return fields
            .filter(field => !field.name.endsWith('_id') && displayedIds.includes(field.id) && textFieldTypes.includes(this.cmpSharedSvc.getFieldTypeFromField(field)))
            .map(({name}) => name);
    }

    isPrintServerVersionSupported(versionInfo: IVersionInfo): IIsPrintServerVersionSupportedResult {
        const expectedVersion = { ...MIN_PRINT_SERVER_VERSION };
        const isSupported =
            versionInfo.major === expectedVersion.major
            && versionInfo.minor >= expectedVersion.minor
            && versionInfo.patch >= expectedVersion.patch;
        const shouldUpgrade = versionInfo.major !== expectedVersion.major;
        const result: IIsPrintServerVersionSupportedResult = {
            expectedVersion: { ...MIN_PRINT_SERVER_VERSION },
            reportedVersion: versionInfo,
            isSupported: isSupported,
            shouldUpgrade: shouldUpgrade,

        };
        return result;
    }

    getCreatedAtField(): IField {
        return {
            id: '__created_at', name: 'created_at', label: this.translate(TranslationKey.createdAt), editable: false,
            schema: { type: 'string', format: 'date-time' }
        } as IField;
    }

    getUpdatedAtField(): IField {
        return {
            id: '__updated_at', name: 'updated_at', label: this.translate(TranslationKey.updatedAt), editable: false,
            schema: { type: 'string', format: 'date-time' }
        } as IField;
    }

    orderedBy<TItem>(items: TItem[], valueSelector: (item: TItem) => string): void {
        items.sort((first, second) => valueSelector(first) < valueSelector(second) ? -1 : 1);
    }

    getOrderedItemsByAttribute<TItem>(
        items: TItem[], attributesOrder: string[], attributeValueSelector: (item: TItem) => string
    ): TItem[] {
        const orderedItems: TItem[] = [];
        for (const attribute of attributesOrder) {
            const item = items.find(x => attributeValueSelector(x) === attribute);
            if (item) {
                orderedItems.push(item);
            }
        }
        return orderedItems;
    }

    createDefaultPagingOptionsForLongLists(): IPagingOptions {
        const result: IPagingOptions = {
            limit: 50, offset: 0, object_count: 0, pageSizes: this.getPageSizes()
        };
        return result;
    }

    createSearchParams(criteria: IAdvancedFilterCriteria[]): SearchParams {
        const searchParams = flatSearchToRecursive(criteria as unknown as FlatSearchParams, 'name') as SearchParams;
        return searchParams;
    }

    removeNullOrUndefinedEnumItemFromFields(fields: IField[]): void {
        for (const field of fields) {
            if (field.schema.enum) {
                field.schema.enum = field.schema.enum.filter(x => x !== null && x !== undefined);
            }
        }
    }

    clone<T>(obj: T): T {
        return JSON.parse(JSON.stringify(obj));
    }

    sortByArray<TItem, TKey>(items: TItem[], propertiesOrder: TKey[], propertyOrderKeySelector: (item: TItem) => TKey): TItem[] {
        const sorted: TItem[] = [];
        const notSorted: TItem[] = [];
        for (const currentPropertyOrder of propertiesOrder) {
            const sortableItem = items.find(x => propertyOrderKeySelector(x) === currentPropertyOrder);
            if (sortableItem) {
                sorted.push(sortableItem);
            } else {
                notSorted.push(sortableItem);
            }
        }
        const result = sorted.concat(notSorted);
        return result;
    }

    async delay(ms: number): Promise<void> {
        return delay(ms);
    }

    createSelectField<SelectValue extends string | number>(
        field: SelectField,
        enumTranslationsPair: Record<SelectValue,
        TranslationKey | TranslationMapKey>
    ): IField {
        const enumKeys = Object.keys(enumTranslationsPair);
        return {
            type: 'select',
            editable: true,
            schema: {
              type: typeof enumKeys[0] === 'number' ? 'number' : 'string',
              enum: enumKeys
            },
            ...field,
            attributeTranslations: Object.entries(enumTranslationsPair).map(([enumKey, translationKey]) => ({
                value: enumKey,
                trans: {
                    [this.translateSvc.currentLang]: this.translate(translationKey as TranslationMapKey)
                }
            }))
        } as IField;
    }

    createDefaultObjectEnumInputFieldValue(
        allItems: IObjectWithId[], selectedId: string, reportProperty?: string, displayProperty: string = 'name'
    ): IObjectEnumInputFieldValue {
        this.sortAlphabetically(allItems, x => x.name);
        const result: IObjectEnumInputFieldValue = {
            displayProperty: displayProperty,
            items: allItems,
            selected: allItems.find(x => x.id === selectedId)
        };
        if (reportProperty) {
            result.reportProperty = reportProperty;
        }
        return result;
    }

    createDefaultObjectEnumInputFieldValueReportingId(allItems: IObjectWithId[], selectedId: string): IObjectEnumInputFieldValue {
        return this.createDefaultObjectEnumInputFieldValue(allItems, selectedId, 'id');
    }

    createDefaultObjectEnumFieldNoNull(id: string, labelTranslationKey?: TranslationKey, name?: string): IField {
        const field = this.createDefaultObjectEnumField(id, labelTranslationKey, name);
        field.schema = { type: 'object' };
        return field;
    }

    createDefaultObjectEnumField(id: string, labelTranslationKey?: TranslationKey, name?: string): IField {
        const field = {
            id: id, name: name || id,
            editable: true, component: FieldComponentType.objectEnum,
            schema: { type: ['object', 'null'] }
        } as IField;
        if (labelTranslationKey) {
            field.label = this.translate(labelTranslationKey);
        }
        return field;
    }

    addFieldOptionsForStageField(allStages: IStage[], fieldsOptions: IFieldIdWithOptions[]): void {
        const currentUserLanguage = this.authSvc.user$()?.language;
        const stagesMap: IValueTranslation = {};
        allStages.forEach(x => stagesMap[x.id] = x.name);
        const stageFieldOptions: IFieldIdWithOptions = {
            fieldId: BuiltInFieldName.stage_id,
            options: {
                translations: {
                    [currentUserLanguage]: stagesMap
                }
            }
        };
        fieldsOptions.push(stageFieldOptions);
    }

    getFilteredColumnsForLists(fields: IField[]): IField[] {
        const filteredFields: IField[] = [];
        for (const field of fields) {
            const fieldType = this.cmpSharedSvc.getFieldTypeFromField(field);
            if (fieldType !== 'document' && fieldType !== 'image') {
                filteredFields.push(field);
            }
        }
        return filteredFields;
    }

    getOrganizationIdFieldName(): string {
        return BuiltInFieldName.organization_id;
    }

    async getOrganizationsByIds(ids: string[]): Promise<IOrganization[]> {
        const searchOrgReq: ISearchOrganizationsRequest = {
            limit: ids.length, offset: 0
        };
        searchOrgReq.advancedQuery = this.createSingleItemSearchParams('id', FieldOperator.in, ids);;
        return (await this.clientSvc.organizationsCollection.getManyWithReq('default',searchOrgReq)).data;
    }

    addParticipantStageIdField(fields: IField[], allStages: IStage[]): void {
        // filter out eventual field stage_id
        for (let i = fields.length - 1; i >= 0; i--) {
            if (fields[i].name === BuiltInFieldName.stage_id) {
                fields.splice(i, 1);
            }
        }
        // add artificial field stage_name that can't be edited
        fields.push({
            id: BuiltInFieldName.stage_id, name: BuiltInFieldName.stage_id, editable: false,
            category: '', label: this.translate(TranslationKey.stage),
            // eslint-disable-next-line @typescript-eslint/naming-convention
            schema: { type: 'string', enum: allStages.map(x => x.id) }, builtin: true, visible_for_planners: true
        });
    }

    setDateTimeFieldsTimeZone(fields: IField[], timeZone: string): void {
        fields.filter(x => this.isDateTimeField(x)).forEach(x => x.timezone = timeZone);
    }

    isDateTimeField(field: IField): boolean {
        return field.schema && field.schema.format === 'date-time';
    }

    filterOutRelationFields(fields: IField[]): IField[] {
        return fields.filter(x => (x.type as unknown) !== 'relation');
    }

    getFilteredColumnsForAdvancedFilter(fields: IField[]): IField[] {
        const filteredFields: IField[] = [];
        for (const field of fields) {
            const fieldType = this.cmpSharedSvc.getFieldTypeFromField(field);
            if (fieldType !== 'document') {
                filteredFields.push(field);
            }
        }
        return filteredFields;
    }

    filterNotAllowed(existing: string[], allowed: string[]): string[] {
        const filtered = existing.filter(x => allowed.indexOf(x) >= 0);
        return filtered;
    }

    getDebounceTimeObservable<TValue>(observable: Observable<TValue>, time: number = 500): Observable<TValue> {
        return observable.pipe(debounceTime(time));
    }

    getDisplayedColumns(allColumns: IField[], savedColumnIds: string[], defaultColumnNames: string[]): string[] {
        const columnNames: string[] = [];
        if (savedColumnIds && savedColumnIds.length > 0) {
            for (const savedColumnId of savedColumnIds) {
                const column = allColumns.find(x => x.id === savedColumnId);
                if (column) {
                    columnNames.push(column.name);
                }
            }
        } else {
            columnNames.push(...defaultColumnNames);
        }
        const displayedColumns: string[] = [];
        for (const columnName of columnNames) {
            const column = allColumns.find(x => x.name === columnName);
            if (column) {
                displayedColumns.push(column.id);
            }
        }
        return displayedColumns;
    }

    createApiServiceConfiguration(baseUrl: string): IApiServiceConfig {
        const cfg: IApiServiceConfig = {
            crmBaseUrl: baseUrl + '/3.1/',
            baseUrl: baseUrl + '/3.0/',
            threeDotOneBaseUrl: environment.threeDotOneBaseUrl,
            baseUrl_4_0: baseUrl + '/4.0/',
            // eslint-disable-next-line @typescript-eslint/naming-convention
            client_id: environment.clientId
        };
        return cfg;
    }

    // openBottomSheet<TComponent, TComponentData, TComponentResult>(
    //     componentType: ComponentType<TComponent>,
    //     componentData?: TComponentData,
    //     additionalPanelClasses?: string[]
    // ): MatBottomSheetRef<TComponent, TComponentResult> {
    //     return this.matBottomSheet.open(
    //         componentType, { data: componentData, panelClass: [CssClassName.bottomSheetPanel, ...additionalPanelClasses || []] }
    //     );
    // }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getDistinctArray<T>(source: T[], keySelector?: (item: T) => any): T[] {
        if (!keySelector) {
            return Array.from(new Set(source));
        }
        const distinctArr: T[] = [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const hashObj: any = {};
        for (const item of source) {
            const key = keySelector ? keySelector(item) : item;
            if (!hashObj[key]) {
                hashObj[key] = true;
                distinctArr.push(item);
            }
        }
        return distinctArr;
    }

    sortAlphabetically<T>(array: T[], valueSelector: (item: T) => string): void {
        if (!array) {
            return;
        }
        if (array.length <= 1) {
            return;
        }
        array.sort((left, right) =>
            (valueSelector(left) || '').toLowerCase().localeCompare((valueSelector(right) || '').toLowerCase())
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    convertEventsDateTimeFieldValues(events: any[], fields: IField[], dateTimeFormat: string): void {
        // TODO: Support also fiels of type 'date'
        const dateTimeFields = fields.filter(x => this.azavistaSharedSvc.getFieldTypeFromField(x) === 'datetime');
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < events.length; i++) {
            const event = events[i];
            const eventKeys = Object.keys(event);
            for (const eventKey of eventKeys) {
                if (dateTimeFields.find(x => x.name === eventKey)) {
                    // This is a date-time field - convert it
                    event[eventKey] = this.inputFldSvc.getDateWithTimezone(event[eventKey], event.timezone, dateTimeFormat);
                } else {
                    // Not date-time field
                }
            }
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    convertDateTimeFieldValues(items: any[], fields: IField[], timezone: string, dateTimeFormat: string): void {
        // TODO: Support also fiels of type 'date'
        const dateTimeFields = fields.filter(x => this.azavistaSharedSvc.getFieldTypeFromField(x) === 'datetime');
        // eslint-disable-next-line @typescript-eslint/prefer-for-of
        for (let i = 0; i < items.length; i++) {
            const item = items[i];
            const itemKeys = Object.keys(item);
            for (const itemKey of itemKeys) {
                if (dateTimeFields.find(x => x.name === itemKey)) {
                    // This is a date-time field - convert it
                    item[itemKey] = this.inputFldSvc.getDateWithTimezone(item[itemKey], timezone, dateTimeFormat);
                } else {
                    // Not date-time field
                }
            }
        }
    }

    createPagingOptionsFromPagedResponse(pagedResponse: IPagedResponse): IPagingOptions {
        const result: IPagingOptions = {
            limit: pagedResponse.limit, offset: pagedResponse.offset,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            object_count: pagedResponse.object_count, pageSizes: this.getPageSizes()
        };
        return result;
    }

    createDefaultTableSettingsChangedData(): ITableSettingsChangedData {
        const data: ITableSettingsChangedData = {
            paging: this.createDefaultPagingOptions(),
            sorting: this.createDefaultSortingOptions(),
            advancedSearch: {} as IAzavistaAdvancedSearchComponentChangedData,
            pagingChanged: false, sortingChanged: false
        };
        return data;
    }

    createDefaultPagingOptions(): IPagingOptions {
        const result: IPagingOptions = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            limit: 10, offset: 0, object_count: 0, pageSizes: this.getPageSizes()
        };
        return result;
    }

    createDefaultSortingOptions(): ISortingChangedData {
        const data: ISortingChangedData = { column: '', direction: '' };
        return data;
    }

    getPageSizes(): number[] {
        return [10, 20, 50];
    }

    createAdvancedSearchRequest(data: ITableSettingsChangedData): IAdvancedSearchRequest {
        this.setFirstPageIfQuickSearchOrFilterChanged(data);
        const req: IAdvancedSearchRequest = {
            limit: data.paging.limit, offset: data.paging.offset
        };
        if (data.advancedSearch) {
            req.advancedQuery = data.advancedSearch.advancedQuery;
            req.quickSearchText = data.advancedSearch.quickSearchText;
        }
        if (data.sorting.direction) {
            req.sortDirection = data.sorting.direction as SortDirection;
            const field = data.sorting.column as IField;
            req.sortFieldName = field.name;
        }
        return req;
    }

    setFirstPageIfQuickSearchOrFilterChanged(data: ITableSettingsChangedData): boolean {
        if (!data.advancedSearch) {
            return false;
        }
        if (data.advancedSearch.quickSearchTextChanged || data.advancedSearch.filterChanged) {
            data.paging.offset = 0;
            return true;
        }
        return false;
    }

    translate(key: TranslationKey | string, interpolateParams?: InterpolationParameters): string {
        return this.translateSvc.instant(key, interpolateParams);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    createSingleItemSearchParams<DataType = any>(fieldName: keyof DataType, operator: FieldOperator, value: any): SearchParams {
        const params = [{ field: { name: fieldName }, value: value, operator: operator }] as FlatSearchParams;
        return flatSearchToRecursive(params, 'name');
    }

    getFormatDateTime = (dateTime: string | Date, timezone: string, dateTimeFormat: string = 'LL') => {
        dateTime = this.inputFldSvc.getDateWithTimezone(
            dateTime,
            timezone,
        );
        return this.inputFldSvc.getFormattedValue(
            dateTime,
            dateTime,
            'datetime',
            this.translateSvc.getCurrentLang(),
            dateTimeFormat,
            dateTimeFormat,
            dateTimeFormat,
            {},
            []
        );
    };
}

export const getCustomCssForBasedOnStage = (selectedStages: IStage[], cssPrefix = 'column_stage_') =>
    selectedStages.reduce((result, stage) => {
        const subtype = stage.subtype;
        if (subtype) {
            result[stage.id] = `${cssPrefix}${subtype}`;
        }
        return result;
    }, {} as ITableColumnCustomCssMap);

type SelectField = Partial<IField> & Pick<IField, 'label' | 'name'>;

export const getGroupByValue = <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T extends { [key in string]: any },
    K extends keyof T,
>(
    rows: T[],
    fieldName: K,
): { [key in T[K]]?: T[] } => {
    return rows.reduce(
        (group, row) => {
            group[row[fieldName]] = [...(group[row[fieldName]] ?? []), row];
            return group;
        },
        {} as { [key in T[K]]?: T[] },
    );
};

export const getMapByValue = <
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T extends { [key in string]: any },
    K extends keyof T,
>(
    rows: T[],
    fieldName: K,
): { [key in T[K]]?: T } => {
    return rows.reduce(
        (group, row) => {
            group[row[fieldName]] = row;
            return group;
        },
        {} as { [key in T[K]]?: T },
    );
};

type Fn<T, R> = (param: T) => R;
export const getMapByGetter = <
    T,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    G extends Fn<T, any>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    V extends (key: ReturnType<G>, row: T) => any
>(
    rows: T[],
    getterKeyFn: G,
    getterValueFn: V,
// eslint-disable-next-line @typescript-eslint/no-explicit-any
): { [key in ReturnType<G> ]: ReturnType<V> extends ReturnType<Fn<ReturnType<G>, any>> ? ReturnType<V>: never} => {
    return rows.reduce(
        (group, row) => {
            const key = getterKeyFn(row);
            group[key] = getterValueFn(key, row);
            return group;
        },
        {} as { [key in  ReturnType<G>]: ReturnType<V> },
    );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const runPromiseSequentially = async <T extends (() => Promise<any>)[]>(
    promises: T,
) => {
    await promises.reduce(
        async (promiseChain, promise) => {
            await promiseChain;
            await promise();
        },
        Promise.resolve()
      );
}
